import { createSelector } from 'reselect';

import { PAY_VALUE_BY_NAME as PAY_BY } from '../../constants/payby';
import { translate } from '../../i18n';
import {
  BasePaymentMethodFromApi,
  PaymentMethod,
  PaymentMethodGroup,
} from '../../types/Payment';
import isExists from '../../utils/isExists';
import { isFiniteNumber } from '../../utils/number';
import { RootState } from '../rootReducer';
import { makeDirectPayDefaultCard } from './directPayCardSelectors';
import { submitBillReducerRaw } from './transactionDetail';

const checkoutReducerRow = (state: RootState) => state?.checkoutReducer || {};

export const makeCheckoutMethodRaw = () => {
  return createSelector(checkoutReducerRow, data => {
    return data?.paymentMethods || {};
  });
};

enum PAY_GROUP {
  CARD = 'CARD',
  ATM = 'ATM',
  CVS = 'CVS',
  LINE_PAY = 'LINE_PAY',
  COD = 'COD',
  APPLE_PAY = 'APPLE_PAY',
}

const GROUP_BY_PAY_BY = {
  [PAY_BY.DIRECT_PAY_TOKEN]: PAY_GROUP.CARD,
  [PAY_BY.DIRECT_PAY_PRIME]: PAY_GROUP.CARD,
  [PAY_BY.CREDIT_CARD]: PAY_GROUP.CARD,
  [PAY_BY.ATM]: PAY_GROUP.ATM,
  [PAY_BY.SINOPAC_VIRTUAL_ACCOUNT]: PAY_GROUP.ATM,
  [PAY_BY.CONVENIENCE_STORE]: PAY_GROUP.CVS,
  [PAY_BY.LINE_PAY]: PAY_GROUP.LINE_PAY,
  [PAY_BY.COD]: PAY_GROUP.COD,
  [PAY_BY.APPLE_PAY]: PAY_GROUP.APPLE_PAY,
};

const payByOrder = [
  PAY_BY.DIRECT_PAY_TOKEN,
  PAY_BY.DIRECT_PAY_PRIME,
  PAY_BY.CREDIT_CARD,
  PAY_BY.ATM,
  PAY_BY.SINOPAC_VIRTUAL_ACCOUNT,
  PAY_BY.CONVENIENCE_STORE,
  PAY_BY.LINE_PAY,
  PAY_BY.COD,
  PAY_BY.APPLE_PAY,
];

type MethodConfigFunction<P, R> = (method: P) => R;

interface MethodConfig {
  is_allowed?: MethodConfigFunction<PaymentMethod, boolean>;
  maintaining_text?: MethodConfigFunction<PaymentMethod, boolean>;
  warnings?: MethodConfigFunction<PaymentMethod, string[]>;
  text?: MethodConfigFunction<PaymentMethod, string>;
}

export const methodConfig: MethodConfig = {
  is_allowed: data => {
    if (Object.keys(data.errors || {}).length > 0) {
      return false;
    }

    return true;
  },
  warnings: data => {
    const errors = Object.values(data.errors || {});

    if (errors.length > 0) {
      return errors;
    }

    return data.warnings || [];
  },
};

type GroupConfigFunction<T> = (newData: T, originData?: T) => T;

interface GroupConfig {
  is_allowed: GroupConfigFunction<boolean>;
  maintaining_text: GroupConfigFunction<string>;
  warnings: GroupConfigFunction<string[]>;
  text: GroupConfigFunction<string>;
}

const groupConfig: Record<string, GroupConfig> = {
  CARD: {
    // 目前： group 內有一個 is_allowed = false，都會 false
    is_allowed: (newData, originData = true) => {
      return !originData ? originData : newData;
    },
    // 目前： group 內有多個 maintaining_text 的情況，後面會覆蓋原來的資訊
    maintaining_text: (newData, originData = '') => {
      return isExists(newData) && newData.length ? newData : originData;
    },
    // 目前： group 內有多個 warning 的情況，後面會覆蓋原來的資訊
    warnings: (newData, originData = []) => {
      return isExists(newData) && newData.length ? newData : originData;
    },
    // 目前： 可自由指定
    text: () => {
      return translate('payment-group-card');
    },
  },
  // Configure if needed
  ATM: {
    is_allowed: () => false,
    maintaining_text: () => '',
    warnings: () => [],
    text: () => '',
  },
  // Configure if needed
  CVS: {
    is_allowed: () => false,
    maintaining_text: () => '',
    warnings: () => [],
    text: () => '',
  },
  LINE_PAY: {
    is_allowed: () => false,
    maintaining_text: () => '',
    warnings: () => [],
    text: () => '',
  },
};

export const getPaymentMethods = () => {
  return createSelector(
    [makeCheckoutMethodRaw(), makeDirectPayDefaultCard()],
    (
      data: { [payBy: string]: BasePaymentMethodFromApi },
      directPayDefaultCard
    ) => {
      // 指定需要放到 payMethodGroup 的 pay_by，以及 override config
      const subMethodsDetail: Record<string, any> = {
        CARD: {
          [PAY_BY.DIRECT_PAY_TOKEN]: directPayDefaultCard,
          [PAY_BY.DIRECT_PAY_PRIME]: {
            text: translate('creditCardPaymentPage'),
          },
          [PAY_BY.CREDIT_CARD]: {
            text: translate('payment-group-card'),
          },
        },
        ATM: {},
        CVS: {},
        LINE_PAY: {},
        APPLE_PAY: {},
      };

      const payMethodGroup = {};
      const result: Array<PaymentMethod & PaymentMethodGroup> = [];
      const payGroupCount = {};

      Object.keys(data)
        .filter(key => {
          const payBy = Number(key);
          const groupName = GROUP_BY_PAY_BY[payBy];

          payGroupCount[groupName] = (payGroupCount[groupName] || 0) + 1;

          return isFiniteNumber(payBy) && !!groupName;
        })
        .forEach(key => {
          const item = data[key];
          const payBy = Number(key);
          const groupName = GROUP_BY_PAY_BY[payBy];
          const details = subMethodsDetail?.[groupName]?.[payBy];

          let method: PaymentMethod | PaymentMethodGroup;

          // 判斷是否在 subMethodsDetail
          if (details) {
            if (Object.keys(details).length === 0) {
              // do not add if no details, like empty DIRECT_PAY_TOKEN
              return;
            }

            method = {
              ...item,
              // 當有多種同類型的付款方式才覆寫
              ...(payGroupCount[groupName] > 1 ? details : {}),
              groupName,
              pay_by: payBy,
            };
          } else {
            method = {
              ...item,
              groupName,
              pay_by: payBy,
            };
          }

          // apply config to method
          Object.keys(methodConfig).forEach(configKey => {
            const info = methodConfig[configKey](method);

            method[configKey] = info;
          });

          payMethodGroup[groupName] = payMethodGroup[groupName]
            ? payMethodGroup[groupName].concat(method)
            : [method];
        });

      // 加入 payMethodGroup 的 method
      Object.keys(payMethodGroup).forEach(key => {
        const groupName = PAY_GROUP[key];
        const methodGroup: PaymentMethod[] = payMethodGroup[groupName];

        if (methodGroup.length === 1) {
          result.push(methodGroup[0]);
        } else if (methodGroup.length > 1) {
          // sort the group methods
          const sortMethodGroup = methodGroup.sort(
            (a, b) =>
              payByOrder.indexOf(a.pay_by) - payByOrder.indexOf(b.pay_by)
          );

          let groupInfo: any = {};

          // apply config to group
          sortMethodGroup.forEach(item => {
            const config: GroupConfig = groupConfig[groupName];

            groupInfo = Object.keys(config).reduce((info, itemKey) => {
              const itemInfo = config[itemKey](item[itemKey], info[itemKey]);

              return {
                ...info,
                [itemKey]: itemInfo,
              };
            }, groupInfo);
          });

          // push to result
          result.push({
            ...groupInfo,
            groupName,
            subMethods: sortMethodGroup,
          });
        }
      });

      // groupName 排序
      const groupNameOrder = Object.values(PAY_GROUP);
      const sortResult = result.sort(
        (a, b) =>
          groupNameOrder.indexOf(a.groupName as PAY_GROUP) -
          groupNameOrder.indexOf(b.groupName as PAY_GROUP)
      );

      return sortResult;
    }
  );
};

export const makeSelectedPaymentMethodWithConfig = () => {
  return createSelector(
    submitBillReducerRaw,
    makeCheckoutMethodRaw(),
    (submitBillData, paymentMethods) => {
      const payBy = submitBillData?.pay_by;
      const currentPaymentMethod =
        typeof payBy !== 'undefined' && paymentMethods[payBy];

      if (
        !currentPaymentMethod ||
        Object.keys(currentPaymentMethod).length === 0
      ) {
        return null;
      }

      let configMethod = { ...currentPaymentMethod };

      configMethod = Object.keys(methodConfig).reduce((info, itemKey) => {
        const itemInfo = methodConfig[itemKey](currentPaymentMethod);

        return {
          ...info,
          [itemKey]: itemInfo,
        };
      }, configMethod);

      return configMethod;
    }
  );
};

export const getDirectPaymentMethods = () =>
  createSelector(getPaymentMethods(), paymentMethods => {
    const cardList = paymentMethods.find(val => val?.groupName === 'CARD');

    if (cardList && Object.keys(cardList).length > 0) {
      const subMethods = cardList?.subMethods;

      return subMethods || [cardList];
    }

    return [];
  });

// checkoutMethodRaw 從 v4/payments 取得
// - 部分付款方式和購物車資料有關（如：超商付款）
// - 在訂單頁面請謹慎使用此資料
export const makePaymentMethodByValue = (thePayByValue: number) =>
  createSelector(makeCheckoutMethodRaw(), paymentMethods => {
    return paymentMethods[thePayByValue];
  });
